import React, { useState } from "react";
import { FieldArray, Formik } from "formik";
import { useMutation } from "@apollo/client";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate, useParams } from "react-router-dom";
import Alert, { AlertColor } from "@mui/material/Alert/Alert";
import { useTranslation } from "react-i18next";
import { FormActionButtons, Loader, NotFound, Notification, Typography } from "components";
import {
  CREATE_FILE_UPLOAD,
  CREATE_FILE_UPLOAD_data,
  CREATE_FILE_UPLOAD_vars,
} from "graphql/mutation/createFileUpload";
import { HouseholdMemberShort } from "graphql/query/caseByIdShort";
import { CONFIRM_FILE_UPLOAD } from "graphql/mutation/confirmFileUplaod";
import { routes } from "routes";
import { getFileExtensionWithDot } from "utils/strings";


import { Form, FormContent, MemberRows } from "../Forms.css";
import { documentKeyByType, validationSchema } from "./formUtils";
import { AlertConsent, ButtonContainer, Description } from "./UploadDocuments.css";
import { UploadDocumentRow } from "./UploadDocumentRow";
import { useUploadDocumentsMembers } from "./useUploadDocumentsMembers";


export interface UploadFormValues {
  uploadDocumentsMembers: HouseholdMemberShort[];
}

export const UploadDocuments = () => {
  const {t} = useTranslation();
  const {id} = useParams<{ id: string }>();
  const caseId = String(id);
  const navigate = useNavigate();

  const {
    loading,
    error,
    data: householdMemberData,
    transformedHouseholdMembers,
  } = useUploadDocumentsMembers(caseId);

  const [createFileUpload] = useMutation<
    CREATE_FILE_UPLOAD_data,
    CREATE_FILE_UPLOAD_vars
  >(CREATE_FILE_UPLOAD);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [confirmUpload] = useMutation(CONFIRM_FILE_UPLOAD);
  const [messageType, setMessageType] = React.useState<AlertColor>("success");
  const [uploadMessage, setUploadMessage] = React.useState<string | null>(null);

  if (loading || isLoading) return <Loader/>;
  if (!loading && !householdMemberData && error) {
    return <NotFound/>;
  }

  const onSubmit = async ({uploadDocumentsMembers}: UploadFormValues) => {
    console.log(uploadDocumentsMembers, 'uploadDocumentsMembers')
    setIsLoading(true);
    try {
      for (const member of uploadDocumentsMembers) {
        for (const document of member.documentsList) {
          const documentUploadType =
            documentKeyByType[document.documentUploadType];
          const fileExtension = getFileExtensionWithDot(
            document?.file?.name ?? "",
          );

          const variables = {
            caseAccessId: caseId,
            fileExtension: fileExtension,
            householdMemberId: member.householdMemberId ?? null,
            documentDescription: document.documentDescription,
            documentUploadType: documentUploadType,
          };

          const createFileRes = await createFileUpload({
            variables,
          });

          const url = createFileRes.data?.createFileUpload.url ?? '';
          const fields = createFileRes.data?.createFileUpload.fields ?? '';
          await uploadToS3(document.file, url, fields);
          const confirmResponse = await confirmUpload({
            variables: {
              fileUploadId: createFileRes.data?.createFileUpload.id,
              fileUploadResult: null,
            },
          });
          setIsLoading(false);
          if (confirmResponse.data?.confirmFileUpload?.status) {
            navigate(routes.upload.replace(":id", caseId));
            navigate(routes.consent);
          } else {
            setUploadMessage(`Something went wrong`);
            setMessageType("error");
          }
        }
      }
    } catch (error) {
      setIsLoading(false);
      setUploadMessage(`Error during the upload process:", ${error}`);
      setMessageType("error");
    }
  };

  const uploadToS3 = async (
    file: File | undefined,
    url: string,
    fields: string,
  ) => {
    let isSuccess;
    let responseHeaders = null;
    const formData = new FormData();
    Object.entries(fields).forEach(([key, value]: any) => {
      formData.append(key, value);
    });

    if (file) {
      formData.append("file", file);
    }
    try {
      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });

      isSuccess = response.ok;
      if (!response.ok) {
        const errorMessage = await response.text();
        return new Error(
          `Server responded with ${response.status}: ${errorMessage}`,
        );
      }

      responseHeaders = {
        xAmzId2: response.headers.get("X-Amz-Id-2"),
        xAmzRequestId: response.headers.get("X-Amz-Request-Id"),
        awsDate: response.headers.get("Date"),
        ETag: response.headers.get("ETag"),
      };
    } catch (error) {
      isSuccess = false;
    }
    return {isSuccess, responseHeaders};
  }

  const redirectOnConsentPage = () => {
    navigate(routes.consent);
  }

  return (
    <>
      <Formik
        initialValues={{uploadDocumentsMembers: transformedHouseholdMembers}}
        enableReinitialize={false}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          await onSubmit(values);
        }}
      >
        {({errors, setFieldValue, handleSubmit, values}) => {
          return (
            <Form onSubmit={handleSubmit} autoComplete="off">
              <FieldArray name="uploadDocumentsMembers">
                {() => (
                  <FormContent>
                    <Description>
                      <Alert
                        icon={<InfoOutlinedIcon fontSize="large"/>}
                        severity="info"
                      >
                        {t("upload_documents_description")}
                      </Alert>
                    </Description>
                    <MemberRows>
                      {values.uploadDocumentsMembers.map(
                        (member: any, index: number) => (
                          <FieldArray
                            key={index}
                            name={`uploadDocumentsMembers.${index}.documentsList`}
                          >
                            {({
                                push: addDocument,
                                remove: removeDocument,
                              }) => (
                              <UploadDocumentRow
                                key={index}
                                index={index}
                                member={member}
                                isMainApplicant
                                addDocument={addDocument}
                                removeDocument={removeDocument}
                                errors={errors}
                                setFieldValue={setFieldValue}
                              />
                            )}
                          </FieldArray>
                        ),
                      )}
                    </MemberRows>
                  </FormContent>
                )}
              </FieldArray>
              <ButtonContainer>
                <AlertConsent icon={<InfoOutlinedIcon/>} severity="warning">
                  <Typography variant={'bodyBold'}>notification_sign_consent</Typography>
                </AlertConsent>
                <FormActionButtons
                  leftButtonName='Skip for now'
                  onBack={redirectOnConsentPage}
                  rightButtonName="Upload"
                />
              </ButtonContainer>
            </Form>
          );
        }}
      </Formik>
      <Notification
        type={messageType}
        message={uploadMessage}
        setMessage={setUploadMessage}
      />
    </>
  );
};
